import React, { useEffect, useState } from 'react';
import DateInput from 'components/forms/basefields/DateInput';
import Dropdown from 'components/forms/basefields/Dropdown';
import MultiSelect from 'components/forms/basefields/MultiSelect';
import TextInput from 'components/forms/basefields/TextInput';
import TabSelect from 'components/forms/basefields/TabSelect';
import { ListFilterFieldType } from 'types/ListTypes';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useTranslation } from 'react-i18next';

interface FilterBarProps {
    filters: ListFilterFieldType[];
    activeFilters: Record<string, any>;
    setActiveFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    dropdownData: Record<string, any[]>;
}

const FilterBar: React.FC<FilterBarProps> = ({
    filters,
    activeFilters,
    setActiveFilters,
    dropdownData,
}) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const [debouncedValues, setDebouncedValues] = useState<Record<string, string>>({});

    // Add a slight debounce to search or text filters
    useEffect(() => {
        const timeout = setTimeout(() => {
            Object.entries(debouncedValues).forEach(([name, value]) => {
                setActiveFilters(prev => ({ ...prev, [name]: value }))
            })
        }, 400)

        return () => clearTimeout(timeout)
    }, [debouncedValues])

    // Update the debounced filter value
    const updateDebouncedFilter = (name: string, value: string) => {
        setDebouncedValues(prev => ({ ...prev, [name]: value }))
    }

    // Update the filter
    const updateFilter = (name: string, value: any) => {
        setActiveFilters(prev => ({ ...prev, [name]: value }));
    };

    // Render a specific filter field
    const renderField = (filter: ListFilterFieldType) => {
        switch (filter.type) {
            case 'search':
            case 'text':
                return (
                    <TextInput
                        name={filter.name}
                        placeholder={filter.placeholder ?? ''}
                        value={debouncedValues[filter.name] ?? activeFilters[filter.name] ?? ''}
                        onChange={(value) => {
                            updateDebouncedFilter(filter.name, value)
                        }}
                    />
                );

            case 'date':
                return (
                    <DateInput
                        name={filter.name}
                        value={activeFilters[filter.name] ?? null}
                        onChange={(value) => {
                            updateFilter(filter.name, value);
                        }}
                    />
                );

            case 'daterange': {
                const value = activeFilters[filter.name] ?? ['', ''];

                return (
                    <div className="filter-daterange">
                        <DateInput
                            name={`${filter.name}_from`}
                            placeholder={t('forms.date_from_placeholder')}
                            value={value[0] ? new Date(value[0]) : null}
                            onChange={(date) => {
                                updateFilter(
                                    filter.name,
                                    [
                                        date,
                                        value[1] ?? null,
                                    ]
                                );
                            }}
                        />

                        <DateInput
                            name={`${filter.name}_to`}
                            placeholder={t('forms.date_to_placeholder')}
                            value={value[1] ? new Date(value[1]) : null}
                            onChange={(date) => {
                                updateFilter(
                                    filter.name,
                                    [
                                        value[0] ?? null,
                                        date,
                                    ]
                                );
                            }}
                        />
                    </div>
                );
            }

            case 'tabs': {
                // Use a sentinel for the option that clears the filter, an empty
                // id would make the tab select fall back to another value
                const allOptionId = '__all__';
                const rawValue = activeFilters[filter.name];
                const currentValue = rawValue === undefined || rawValue === null || rawValue === '' ? allOptionId : String(rawValue);

                // Build the options and find the selected one
                const tabOptions = (filter.tabOptions ?? []).map(option => ({
                    id: option.value || allOptionId,
                    name: option.label,
                }));
                const selectedTab = tabOptions.find(option => option.id === currentValue) ?? tabOptions[0] ?? null;

                return (
                    <TabSelect<any>
                        options={tabOptions}
                        selectedOption={selectedTab}
                        optionFormat='name'
                        onChange={(selectedValue) => {

                            // Clear the filter when the all option is selected
                            if (!selectedValue || selectedValue === allOptionId) {
                                setActiveFilters(prev => {
                                    const newFilters = { ...prev };
                                    delete newFilters[filter.name];
                                    return newFilters;
                                });

                                return;
                            }

                            updateFilter(filter.name, selectedValue);
                        }}
                    />
                );
            }

            case 'dropdown': {
                // Get the backend field of the option values
                const backendField = filter.backendField ?? 'id';

                // Get the options from the api request or hardcoded options
                const rawOptions = [
                    ...(dropdownData[filter.apiRequest?.object ?? ''] ?? []),
                    ...(filter.hardcodedOptions ?? []),
                ].filter(option => {
                    // Hide the options that are excluded for this field
                    if (!filter.excludeOptionValues?.length) {
                        return true;
                    }
                    return !filter.excludeOptionValues.includes(String(option[backendField]));
                });

                // When grouping by another filter's field, resolve the readable group name from that filter's own dropdown data
                const groupNameField = `${filter.groupByField}_name`;
                const groupSourceOptions = filter.groupByField ? (dropdownData[filter.groupByField] ?? []) : [];
                const options = filter.groupByField
                    ? rawOptions.map(option => ({
                        ...option,
                        [groupNameField]: groupSourceOptions.find((groupOption: Record<string, any>) => String(groupOption.id) === String(option[filter.groupByField as string]))?.name ?? option[filter.groupByField as string],
                    }))
                    : rawOptions;

                // Restrict to the active group as a flat list, otherwise group the options
                const activeGroupValue = filter.groupByField ? activeFilters[filter.groupByField] : undefined;
                const groupedOptions = activeGroupValue
                    ? options.filter(option => String(option[filter.groupByField as string]) === String(activeGroupValue))
                    : options;
                const groupByFieldProp = filter.groupByField && !activeGroupValue ? groupNameField : undefined;

                // Get the selected option based on the backend field value
                const selectedOption = groupedOptions.find(option => String(option[backendField]) === String(activeFilters[filter.name])) ?? null;

                // Get the option field or fallback to name
                const optionField = filter.optionField ?? 'name';

                return (
                    <Dropdown<any>
                        name={filter.name}
                        options={groupedOptions}
                        groupByField={groupByFieldProp}
                        selectedOption={selectedOption}
                        optionFormat={optionField}
                        selectionFormat={(option) => option[optionField] ?? ''}
                        disabled_selected={filter.label}
                        translationPrefix={filter.translationPrefix}
                        noneOptionLabel='forms.all_option'
                        onChange={(value) => {
                            if (value === 'none') {
                                setActiveFilters(prev => {
                                    const newFilters = { ...prev };
                                    delete newFilters[filter.name];
                                    return newFilters;
                                });

                                return;
                            }

                            const selectedOption = options.find((option: Record<string, any>) => String(option.id) === String(value));

                            if (selectedOption) {
                                updateFilter(filter.name, selectedOption[backendField]);
                            }
                        }}
                    />
                );
            }

            case 'multiselect': {
                const options = dropdownData[filter.apiRequest?.object ?? ''] ?? [];
                const optionField = filter.optionField ?? 'name';
                const selectedValues = activeFilters[filter.name] ?? [];
                const selectedOptions = options.filter(option => selectedValues.includes(String(option.id)));

                return (
                    <MultiSelect<any>
                        name={filter.name}
                        options={options}
                        selectedOption={selectedOptions}
                        optionFormat={(option) => option[optionField] ?? ''}
                        selectionFormat={(option) => option[optionField] ?? ''}
                        disabled_selected={filter.label}
                        onChange={(value) => {
                            const currentValues = activeFilters[filter.name] ?? [];
                            const exists = currentValues.includes(value);

                            if (exists) {
                                updateFilter(
                                    filter.name,
                                    currentValues.filter(
                                        (currentValue: string) =>
                                            currentValue !== value
                                    )
                                );

                            } else {
                                updateFilter(
                                    filter.name,
                                    [
                                        ...currentValues,
                                        value,
                                    ]
                                );
                            }
                        }}
                    />
                );
            }

            default:
                return null;
        }
    };

    return (
        <div className="filter-bar">
            {filters
                .filter(filter => {
                    if (!filter.requiredFeature) return true;
                    return activeFeatures.includes(filter.requiredFeature);
                })
                .map(filter => (
                    <div
                        key={filter.name}
                        className="filter-field"
                        style={{'--filter-width': filter.width ?? 1.5} as React.CSSProperties}
                    >
                        {renderField(filter)}
                    </div>
                ))
            }
        </div>
    );
};

export default FilterBar;